import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
  ReactNode,
} from "react";
import { useParams } from "react-router-dom";
import { FeatureFlagsResponse, FeatureDefinition } from "../common/types";
import { useWorkspaceFeatureFlags } from "../services/api";

interface FeatureFlagContextType {
  flags: Record<string, boolean>;
  gates: Record<string, boolean>;
  sources: Record<string, string>;
  environment: string;
  featureSchema: Record<string, Record<string, FeatureDefinition>>;
  loading: boolean;
  refreshFlags: () => Promise<void>;
}

const FeatureFlagContext = createContext<FeatureFlagContextType | null>(null);

export const useFeatureFlagsContext = () => {
  const ctx = useContext(FeatureFlagContext);
  if (!ctx) {
    throw new Error(
      "useFeatureFlagsContext must be used within a FeatureFlagProvider",
    );
  }
  return ctx;
};

export const useFeatureFlag = (flagKey: string): boolean => {
  const { flags } = useFeatureFlagsContext();
  return Boolean(flags?.[flagKey]);
};

export const FeatureFlagProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const { workspace_id } = useParams();
  const fetchFeatureFlags = useWorkspaceFeatureFlags();

  const [flags, setFlags] = useState<Record<string, boolean>>({});
  const [gates, setGates] = useState<Record<string, boolean>>({});
  const [sources, setSources] = useState<Record<string, string>>({});
  const [environment, setEnvironment] = useState<string>(""); // for diagnostics
  const [featureSchema, setFeatureSchema] = useState<
    Record<string, Record<string, FeatureDefinition>>
  >({});
  const [loading, setLoading] = useState<boolean>(true);
  const fetchTokenRef = useRef(0);

  const refreshFlags = useCallback(async () => {
    const fetchToken = ++fetchTokenRef.current;

    if (!workspace_id) {
      setFlags({});
      setGates({});
      setSources({});
      setEnvironment("");
      setFeatureSchema({});
      setLoading(false);
      return;
    }

    try {
      const data: FeatureFlagsResponse = await fetchFeatureFlags(workspace_id);
      if (fetchToken !== fetchTokenRef.current) return; // stale response
      setFlags(data.flags || {});
      setGates(data.gates || {});
      setSources(data.sources || {});
      setEnvironment(data.environment || "");
      setFeatureSchema(data.feature_schema || {});
    } catch (error) {
      console.error("Failed to fetch feature flags", error);
      if (fetchToken !== fetchTokenRef.current) return; // stale response
      // Clear stale flags to avoid leaking previous workspace values
      setFlags({});
      setGates({});
      setSources({});
      setEnvironment("");
      setFeatureSchema({});
    } finally {
      if (fetchToken !== fetchTokenRef.current) return; // stale response
      setLoading(false);
    }
  }, [workspace_id, fetchFeatureFlags]);

  useEffect(() => {
    setLoading(true);
    refreshFlags();
  }, [refreshFlags, workspace_id]);

  // Refetch flags when the tab regains focus. Sibling to WorkspaceProvider's
  // Task #554 fix: MM Admin toggles of workspace feature flags (via
  // /api/admin/metalmind/feature-flags) need to propagate to other open tabs
  // without a manual hard refresh. WorkspaceProvider covers permissions/role/
  // mode/modules; this covers the per-workspace feature flag layer.
  useEffect(() => {
    const handleFocus = () => {
      refreshFlags();
    };
    window.addEventListener("focus", handleFocus);
    return () => window.removeEventListener("focus", handleFocus);
  }, [refreshFlags]);

  return (
    <FeatureFlagContext.Provider
      value={{
        flags,
        gates,
        sources,
        environment,
        featureSchema,
        loading,
        refreshFlags,
      }}
    >
      {children}
    </FeatureFlagContext.Provider>
  );
};
